// Protected Route — the superadmin gate.
//
// The source of truth is LIVE storage, not the Redux snapshot. Redux is
// per-tab memory seeded once at load; storage is shared by every tab. Trust
// the snapshot and a logout in another tab leaves this one dressed as a
// superadmin with a dead token — every click a 401 — while the reverse
// bounces a freshly signed-in session to the login page. useAuthPresence
// re-renders this gate the moment another tab signs the superadmin realm in
// or out, so the storage read below is always current.

import React, { useCallback, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { ROUTES, IDLE_TIMEOUT_MS, STORAGE_KEYS } from '../../constants/app.constants';
import StorageUtil from '../../utils/storage';
import useIdleLogout from '../../hooks/useIdleLogout';
import useAuthPresence from '../../hooks/useAuthPresence';
import { forceLogoutRedirect } from '../../utils/sessionGuard';

const WATCHED_KEYS = [STORAGE_KEYS.ACCESS_TOKEN, STORAGE_KEYS.REFRESH_TOKEN];

const ProtectedRoute = ({ children }) => {
  useAuthPresence(WATCHED_KEYS);
  const token = StorageUtil.getAccessToken();
  const tokenExpired = StorageUtil.isTokenExpired(token);

  // Auto-logout after IDLE_TIMEOUT_MS with no activity in ANY tab.
  const handleIdleLogout = useCallback(() => {
    forceLogoutRedirect('inactive');
  }, []);
  useIdleLogout(IDLE_TIMEOUT_MS, handleIdleLogout);

  useEffect(() => {
    const expiresAt = StorageUtil.getTokenExpiry(token);
    if (!expiresAt) return undefined;

    const remainingTime = expiresAt - Date.now();
    if (remainingTime <= 0) return undefined;

    const expiryTimer = window.setTimeout(() => {
      forceLogoutRedirect('session_expired');
    }, remainingTime);

    return () => window.clearTimeout(expiryTimer);
  }, [token]);

  if (!token || tokenExpired) {
    return (
      <Navigate
        to={tokenExpired ? `${ROUTES.LOGIN}?reason=session_expired` : ROUTES.LOGIN}
        replace
      />
    );
  }

  return children;
};

export default ProtectedRoute;
